import {
  validateConfig,
  resolveGooglePayJsEnvironment,
  type PayrSDKConfig,
  type ResolvedPayrSDKConfig,
  type ValidatedPayrSDKConfig,
  WORLDPAY_SDK_URLS,
  DEFAULT_SDK_CURRENCY,
} from './config.js';
import { PayrApiClient, type TenancyResponse } from './api/client.js';
import { loadSDK, clearSDKCache } from './worldpay/adapter.js';
import {
  initCvcCheckout,
  removeCvcCheckout,
  generateCvcSession,
  isCvcCheckoutActive,
} from './worldpay/cvc.js';
import {
  initPaymentMethodsDropdown,
  removePaymentMethodsDropdown,
  getSelectedPaymentMethodId,
  setPaymentMethodsVisible,
  hasSavedPaymentMethods,
} from './ui/paymentMethods.js';
import {
  initPaymentTypeDropdown,
  removePaymentTypeDropdown,
  getSelectedPaymentType,
} from './ui/paymentTypeDropdown.js';
import { initWalletButtonsRow, removeWalletButtonsRow } from './ui/walletButtonsRow.js';
import { completeApmPaymentFlow, closeApmPaymentOverlay } from './ui/apmPaymentFlow.js';
import {
  openAddPaymentMethodModal,
  closeAddPaymentMethodModal,
} from './ui/addPaymentMethodModal.js';
import {
  openDeletePaymentMethodModal,
  closeDeletePaymentMethodModal,
} from './ui/deletePaymentMethodModal.js';
import { closePay3dsOverlay, completePaymentWithOptional3ds } from './ui/pay3dsOverlay.js';
import {
  ensurePayContainerLayout,
  removePayContainerLayout,
  updateMainLayoutCvvHelp,
  setPayrCvcCellVisible,
  syncPaymentBreakdown,
} from './ui/payContainerLayout.js';
import { showSdkInitLoading, removeSdkInitLoading } from './ui/sdkInitLoading.js';
import {
  syncRecurringPaymentStrip,
  removeRecurringPaymentStrip,
  getRecurringToggleState,
  resetRecurringToggleState,
} from './ui/recurringPaymentStrip.js';
import { openRecurringManageModal, closeRecurringManageModal } from './ui/recurringManageModal.js';
import { closeRecurringPauseConfirmModal } from './ui/recurringPauseConfirmModal.js';
import { validateToken } from './utils/security.js';
import { assertBrowserSupported } from './utils/browser.js';
import { setDebugMode } from './utils/logger.js';
import { collectPaymentDeviceData } from './utils/paymentDeviceData.js';
import { computeChargedAmountFromPure, resolvePaymentFeeRateFromEnv } from './utils/paymentFee.js';
import { PayrErrorCode } from './utils/errors.js';
import {
  ApmType,
  PaymentMethod,
  RecurringPaymentDetails,
  RecurringPaymentListItem,
  PayrConfigError,
  PayrError,
  isApmType,
} from './types.js';
import {
  getAmountInputValue,
  validateAmountInput,
  touchAmountInput,
  removeAmountInputBlock,
} from './ui/amountInput.js';
import { checkApplePayAvailability, runApplePayCheckout } from './wallet/applePayFlow.js';
import { loadApplePayScript, removeApplePayScript } from './wallet/applePayScript.js';
import { checkGooglePayAvailability, runGooglePayCheckout } from './wallet/googlePayFlow.js';
import { loadGooglePayScript, removeGooglePayScript } from './wallet/googlePayScript.js';
import {
  pickPrimaryRecurring,
  getHasScheduleForPrimary,
  isActivatedRecurring,
  formatNextPaymentDateLabel,
} from './utils/recurringState.js';
import {
  PAYR_ROOT_CLASS,
  applyAppearanceToContainer,
  clearAppearanceFromContainer,
  DEFAULT_SDK_APPEARANCE,
  setActiveSdkAppearance,
} from './theme/appearance.js';

// Stored for Phase 2+ (payment form, WorldPay init)
let storedConfig: ResolvedPayrSDKConfig | null = null;
let storedClient: PayrApiClient | null = null;

let recurringSnapshot: {
  list: RecurringPaymentListItem[];
  details: RecurringPaymentDetails | null;
} = { list: [], details: null };

const RECURRING_STATUS_CANCELLED = 'cancelled';

function isRecurringStatusCancelled(status: unknown): boolean {
  return String(status).toLowerCase() === RECURRING_STATUS_CANCELLED;
}

function recurringTenancyLabelForDeleteModal(): string | undefined {
  const t = recurringSnapshot.details?.tenancy;
  if (!t) return undefined;
  const line = t.property_address_line_1?.trim();
  if (line) return line;
  const name = t.display_name ? String(t.display_name).trim() : '';
  if (name) return name;
  return undefined;
}

let refreshRecurringUiImpl: (() => Promise<void>) | null = null;
let openAddPaymentMethodRef: (() => void) | null = null;

/** Bumped at each `init()` start and whenever init is torn down so stale async work never commits globals/DOM. */
let initGeneration = 0;

function bumpInitGeneration(): void {
  initGeneration += 1;
}

function assertInitGenerationCurrent(generation: number): void {
  if (generation !== initGeneration) {
    throw new PayrError(
      'PAYR.init() aborted (instance destroyed or superseded).',
      PayrErrorCode.INIT_FAILED
    );
  }
}

async function refreshRecurringState(): Promise<void> {
  if (!storedClient || storedConfig?.features?.recurring !== true) return;
  try {
    const list = await storedClient.getRecurringPayments();
    const p = pickPrimaryRecurring(list);
    let det: RecurringPaymentDetails | null = null;
    if (p) {
      try {
        det = await storedClient.getRecurringPayment(p.id);
        if (isRecurringStatusCancelled(det.status)) det = null;
      } catch {
        det = null;
      }
    }
    recurringSnapshot = { list, details: det };
  } catch (e) {
    storedConfig?.onError?.(e instanceof Error ? e : new Error(String(e)));
    recurringSnapshot = { list: [], details: null };
  }
  await applyRecurringStrip();
}

function syncMainPaymentBreakdown(): void {
  const cfg = storedConfig;
  if (!cfg?.containerElement) return;
  const liveAmount = cfg.isAmountEditable
    ? getAmountInputValue(cfg.containerElement)
    : cfg.pure_amount;
  syncPaymentBreakdown(cfg.containerElement, {
    pureAmountPounds: liveAmount,
    feeRate: cfg.paymentFeeRate,
    visible: true,
  });
}

function hideRecurringStrip(container: HTMLElement): void {
  syncRecurringPaymentStrip({ containerElement: container, visible: false, mode: 'toggle' });
}

/**
 * Prefer details from GET …/:id/ when list item is activated but details are missing
 * (list from GET /worldpay/recurring/ can lag after pay()).
 */
async function hydrateRecurringDetailsIfNeeded(
  primary: RecurringPaymentListItem | null,
  details: RecurringPaymentDetails | null,
  client: PayrApiClient | null
): Promise<RecurringPaymentDetails | null> {
  if (!primary || !isActivatedRecurring(primary) || details != null || !client) {
    return details;
  }
  try {
    const d = await client.getRecurringPayment(primary.id);
    if (isRecurringStatusCancelled(d.status)) return details;
    recurringSnapshot = { ...recurringSnapshot, details: d };
    return d;
  } catch {
    return details;
  }
}

function openManageRecurringModalFromStrip(): void {
  const det = recurringSnapshot.details;
  const p = pickPrimaryRecurring(recurringSnapshot.list);
  if (!storedClient || !storedConfig || !det || !p) return;
  void storedClient.getPaymentMethods().then((pms) => {
    openRecurringManageModal({
      apiClient: storedClient!,
      checkoutId: storedConfig!.checkoutId,
      containerElement: storedConfig!.containerElement,
      recurringId: p.id,
      initialDetails: det,
      paymentMethods: pms,
      paymentFeeRate: storedConfig!.paymentFeeRate,
      debug: storedConfig!.debug,
      onError: (e) => storedConfig!.onError?.(e),
      onUpdated: async () => {
        await refreshRecurringUiImpl?.();
      },
      requestAddPaymentMethod: () => openAddPaymentMethodRef?.(),
      refreshPaymentMethods: () => storedClient!.getPaymentMethods(),
      suspendMainCvcForModal: () => {
        removeCvcCheckout();
      },
      resumeMainCvcAfterModal: () => resumeMainCvcAfterModalFlow(),
    });
  });
}

function showActivatedRecurringStatusStrip(
  container: HTMLElement,
  details: RecurringPaymentDetails
): void {
  const isPaused = String(details.status).toLowerCase() === 'paused';
  const next = formatNextPaymentDateLabel(details.next_payment_date, isPaused);
  syncRecurringPaymentStrip({
    containerElement: container,
    visible: true,
    mode: 'status',
    status: {
      title: isPaused ? 'Recurring payment is paused' : 'Recurring payment is enabled on this rent',
      nextPaymentLabel: `Next payment: ${next}`,
      paymentMethod: details.payment_method,
      isPaused,
    },
    onManage: openManageRecurringModalFromStrip,
  });
}

async function applyRecurringStrip(): Promise<void> {
  if (!storedConfig?.containerElement) return;
  const container = storedConfig.containerElement;
  const cfg = storedConfig;

  try {
    if (cfg.features?.recurring !== true) {
      hideRecurringStrip(container);
      return;
    }

    const primary = pickPrimaryRecurring(recurringSnapshot.list);
    const details = await hydrateRecurringDetailsIfNeeded(
      primary,
      recurringSnapshot.details,
      storedClient
    );

    const hasSchedule = getHasScheduleForPrimary(primary, details);
    if (!hasSchedule) {
      hideRecurringStrip(container);
      return;
    }

    if (primary != null && details != null && isActivatedRecurring(details)) {
      showActivatedRecurringStatusStrip(container, details);
      return;
    }

    if (getSelectedPaymentType() !== 'card' || !hasSavedPaymentMethods()) {
      hideRecurringStrip(container);
      return;
    }

    syncRecurringPaymentStrip({
      containerElement: container,
      visible: true,
      mode: 'toggle',
    });
  } finally {
    syncMainPaymentBreakdown();
  }
}

type SyncCardCvcRowOptions = {
  dispatchPaymentMethodsChanged?: boolean;
};

async function tearDownPmCvcRow(
  cfg: ResolvedPayrSDKConfig,
  pmCvcBlock: HTMLElement | null,
  hidePmRow: boolean
): Promise<void> {
  removeCvcCheckout();
  if (hidePmRow && pmCvcBlock) pmCvcBlock.style.display = 'none';
  setPayrCvcCellVisible(cfg.containerElement, false);
  if (cfg.features?.recurring === true) {
    await applyRecurringStrip();
  }
}

async function initMainCvcIfNeeded(
  cfg: ResolvedPayrSDKConfig,
  cvcHost: HTMLElement
): Promise<void> {
  try {
    if (!isCvcCheckoutActive()) {
      await initCvcCheckout({
        checkoutId: cfg.checkoutId,
        containerElement: cvcHost,
      });
    }
  } catch (err) {
    cfg.onError?.(err instanceof Error ? err : new Error(String(err)));
  }
}

async function afterCardCvcRowReady(
  cfg: ResolvedPayrSDKConfig,
  options?: SyncCardCvcRowOptions
): Promise<void> {
  if (options?.dispatchPaymentMethodsChanged) {
    document.dispatchEvent(new CustomEvent('payr-sdk-payment-methods-changed'));
  }
  if (cfg.features?.recurring === true) {
    await applyRecurringStrip();
  }
}

function isNonCardPaymentUiMode(cfg: ResolvedPayrSDKConfig): boolean {
  const t = getSelectedPaymentType();
  if (t === 'card') return false;
  return cfg.features?.apms === true && isApmType(t);
}

/**
 * Keeps the main CVC row in sync with saved cards (and APM "card" mode).
 */
async function syncCardCvcRowForConfig(
  cfg: ResolvedPayrSDKConfig,
  options?: SyncCardCvcRowOptions
): Promise<void> {
  if (isNonCardPaymentUiMode(cfg)) {
    return;
  }
  const { mainStack, cvcHost: host } = ensurePayContainerLayout(cfg.containerElement);
  const pmCvcBlock = mainStack.querySelector<HTMLElement>('.payr-pm-cvc-block');
  const hasPms = hasSavedPaymentMethods();

  if (pmCvcBlock) pmCvcBlock.style.display = '';
  if (!hasPms) {
    await tearDownPmCvcRow(cfg, pmCvcBlock, false);
    return;
  }
  setPayrCvcCellVisible(cfg.containerElement, true);
  await initMainCvcIfNeeded(cfg, host);
  await afterCardCvcRowReady(cfg, options);
}

/**
 * WorldPay allows only one checkout.init at a time. After modal CVV uses modalCvc, restore main CVC.
 */
async function resumeMainCvcAfterModalFlow(): Promise<void> {
  if (!storedConfig) return;
  await syncCardCvcRowForConfig(storedConfig);
}

/** Serialized refresh after add-card success; await before re-init CVC on modal close. */
let inFlightPaymentMethodsRefresh: Promise<void> | null = null;

function resolveTenancy(tenancies: TenancyResponse[]): {
  tenancyId: string;
  selectedTenancy: TenancyResponse;
} {
  if (!tenancies.length) {
    throw new PayrError('No tenancies found', 'CONFIG_FETCH_FAILED');
  }
  const selected =
    tenancies.length === 1
      ? tenancies[0]
      : (tenancies.find((t) => t.is_primary === true) ?? tenancies[0]);
  return { tenancyId: String(selected.id), selectedTenancy: selected };
}

async function resolveCheckoutIdAndEnvironment(
  validated: ValidatedPayrSDKConfig,
  newClient: PayrApiClient
): Promise<{ checkoutId: string; environment: 'sandbox' | 'production' }> {
  if (validated.checkoutId) {
    return {
      checkoutId: validated.checkoutId,
      environment: validated.environment ?? 'production',
    };
  }
  const remote = await newClient.getSdkConfig();
  return {
    checkoutId: remote.checkoutId,
    environment: validated.environment ?? remote.environment,
  };
}

async function fetchPrimaryRecurringDetailsForInit(
  client: PayrApiClient,
  rlist: RecurringPaymentListItem[],
  generation: number
): Promise<RecurringPaymentDetails | null> {
  const p = pickPrimaryRecurring(rlist);
  if (!p) return null;
  try {
    const det = await client.getRecurringPayment(p.id);
    if (generation !== initGeneration) return null;
    return isRecurringStatusCancelled(det.status) ? null : det;
  } catch {
    return null;
  }
}

async function loadInitialRecurringSnapshotForInit(
  newClient: PayrApiClient,
  validated: ValidatedPayrSDKConfig,
  newConfig: ResolvedPayrSDKConfig,
  generation: number
): Promise<void> {
  if (generation !== initGeneration) {
    return;
  }
  recurringSnapshot = { list: [], details: null };
  if (validated.features?.recurring !== true) {
    return;
  }
  try {
    const rlist = await newClient.getRecurringPayments();
    if (generation !== initGeneration) {
      return;
    }
    const det = await fetchPrimaryRecurringDetailsForInit(newClient, rlist, generation);
    if (generation !== initGeneration) {
      return;
    }
    recurringSnapshot = { list: rlist, details: det };
  } catch (e) {
    if (generation !== initGeneration) {
      return;
    }
    newConfig.onError?.(e instanceof Error ? e : new Error(String(e)));
  }
}

function clearSdkAppearanceMount(containerElement: HTMLElement): void {
  containerElement.classList.remove(PAYR_ROOT_CLASS);
  clearAppearanceFromContainer(containerElement);
  setActiveSdkAppearance(DEFAULT_SDK_APPEARANCE);
}

function teardownInitOnInnerFailure(containerElement: HTMLElement, sdkUrl: string): void {
  bumpInitGeneration();
  removeSdkInitLoading(containerElement);
  clearSdkAppearanceMount(containerElement);
  removeCvcCheckout();
  removeApplePayScript();
  removeGooglePayScript();
  removeWalletButtonsRow();
  removePaymentMethodsDropdown();
  removePaymentTypeDropdown();
  removeRecurringPaymentStrip();
  removeAmountInputBlock(containerElement);
  removePayContainerLayout(containerElement);
  clearSDKCache(sdkUrl);
  storedClient = null;
  storedConfig = null;
  refreshRecurringUiImpl = null;
  openAddPaymentMethodRef = null;
  recurringSnapshot = { list: [], details: null };
}

function schedulePaymentMethodsRefresh(): Promise<void> {
  if (!storedClient || !storedConfig) {
    return Promise.resolve();
  }
  const cfg = storedConfig;
  const client = storedClient;

  const run = async (): Promise<void> => {
    const methods = await client.getPaymentMethods();
    const { paymentMethodsHost: pmHost } = ensurePayContainerLayout(cfg.containerElement);
    removePaymentMethodsDropdown();
    initPaymentMethodsDropdown(pmHost, methods, () => {}, {
      onAddNew: openAddPaymentMethod,
      showLabel: false,
      onSelectedPaymentMethodChange: (m) => {
        updateMainLayoutCvvHelp(cfg.containerElement, m?.schema);
      },
      onDeleteCardRequested: requestDeletePaymentMethod,
    });
    if (isNonCardPaymentUiMode(cfg)) {
      setPaymentMethodsVisible(false);
      if (cfg.features?.recurring === true) {
        await applyRecurringStrip();
      } else {
        syncMainPaymentBreakdown();
      }
      document.dispatchEvent(new CustomEvent('payr-sdk-payment-methods-changed'));
      return;
    }
    await syncCardCvcRowForConfig(cfg, { dispatchPaymentMethodsChanged: true });
  };

  const p = run().catch((err) => {
    cfg.onError?.(err instanceof Error ? err : new Error(String(err)));
  });
  inFlightPaymentMethodsRefresh = p;
  void p.finally(() => {
    if (inFlightPaymentMethodsRefresh === p) {
      inFlightPaymentMethodsRefresh = null;
    }
  });
  return p;
}

function requestDeletePaymentMethod(m: PaymentMethod): void {
  if (!storedClient || !storedConfig) return;
  const client = storedClient;
  const cfg = storedConfig;
  openDeletePaymentMethodModal({
    recurringTenancyLabel: recurringTenancyLabelForDeleteModal(),
    onConfirm: async () => {
      await client.deletePaymentMethod(m.token_id);
      await schedulePaymentMethodsRefresh();
    },
    onError: (err) => cfg.onError?.(err),
  });
}

function openAddPaymentMethod(): void {
  if (!storedClient || !storedConfig) return;
  removeCvcCheckout();
  openAddPaymentMethodModal({
    checkoutId: storedConfig.checkoutId,
    apiClient: storedClient,
    tenancy: storedConfig.selectedTenancy,
    debug: storedConfig.debug,
    onSuccess: () => {
      void schedulePaymentMethodsRefresh();
    },
    onError: (err) => storedConfig?.onError?.(err),
    onClose: () => {
      void (async () => {
        if (inFlightPaymentMethodsRefresh != null) {
          try {
            await inFlightPaymentMethodsRefresh;
          } catch {
            /* Errors reported via storedConfig.onError in schedulePaymentMethodsRefresh */
          }
        }
        const cfg = storedConfig;
        if (!cfg) return;
        await syncCardCvcRowForConfig(cfg);
      })();
    },
  });
}

async function syncPaymentModeUiAfterTypeChange(): Promise<void> {
  if (!storedConfig) return;
  if (getSelectedPaymentType() === 'card') {
    setPaymentMethodsVisible(true);
    await syncCardCvcRowForConfig(storedConfig);
    return;
  }
  resetRecurringToggleState(storedConfig.containerElement);
  removeCvcCheckout();
  setPaymentMethodsVisible(false);
  if (storedConfig.features?.recurring === true) {
    await applyRecurringStrip();
  }
}

async function applyInitialCvcAndRecurringLayout(options: {
  validated: ValidatedPayrSDKConfig;
  newConfig: ResolvedPayrSDKConfig;
  mainStack: HTMLElement;
  paymentMethodsCount: number;
  generation: number;
}): Promise<void> {
  const { validated, newConfig, mainStack, generation } = options;
  assertInitGenerationCurrent(generation);
  const pmCvcBlockInit = mainStack.querySelector<HTMLElement>('.payr-pm-cvc-block');
  const cardUiActive = !validated.features?.apms || getSelectedPaymentType() === 'card';
  if (!cardUiActive) {
    removeCvcCheckout();
    setPayrCvcCellVisible(newConfig.containerElement, false);
    if (validated.features?.recurring === true) {
      await applyRecurringStrip();
      assertInitGenerationCurrent(generation);
    }
    return;
  }
  if (pmCvcBlockInit) pmCvcBlockInit.style.display = '';
  await syncCardCvcRowForConfig(newConfig);
  assertInitGenerationCurrent(generation);
}

type InitWalletFeatureFlags = {
  appleOn: boolean;
  googleOn: boolean;
};

type InitWalletAvailability = {
  applePayAvailable: boolean;
  googlePayAvailable: boolean;
  googlePayJsEnv: ReturnType<typeof resolveGooglePayJsEnvironment>;
};

function wrapWalletButtonHandler(runCheckout: () => Promise<void>): () => Promise<void> {
  return async () => {
    try {
      await runCheckout();
    } catch (err) {
      storedConfig?.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  };
}

async function probeInitWalletAvailability(
  flags: InitWalletFeatureFlags,
  environment: ResolvedPayrSDKConfig['environment'],
  generation: number
): Promise<InitWalletAvailability> {
  let applePayAvailable = false;
  if (flags.appleOn) {
    await loadApplePayScript();
    assertInitGenerationCurrent(generation);
    applePayAvailable = await checkApplePayAvailability();
    assertInitGenerationCurrent(generation);
  }

  let googlePayAvailable = false;
  const googlePayJsEnv = resolveGooglePayJsEnvironment(environment);
  if (flags.googleOn) {
    try {
      await loadGooglePayScript();
      assertInitGenerationCurrent(generation);
      googlePayAvailable = await checkGooglePayAvailability({
        environment: googlePayJsEnv,
      });
      assertInitGenerationCurrent(generation);
    } catch {
      assertInitGenerationCurrent(generation);
      googlePayAvailable = false;
    }
  }

  return { applePayAvailable, googlePayAvailable, googlePayJsEnv };
}

function mountInitWalletButtonsRow(options: {
  mainStack: HTMLElement;
  validated: ValidatedPayrSDKConfig;
  flags: InitWalletFeatureFlags;
  availability: InitWalletAvailability;
}): void {
  const { mainStack, validated, flags, availability } = options;
  const showWalletSection =
    (flags.googleOn && availability.googlePayAvailable) ||
    (flags.appleOn && availability.applePayAvailable);
  if (!showWalletSection) {
    return;
  }

  const pa = validated.pure_amount;
  const hasInitAmount = typeof pa === 'number' && Number.isFinite(pa) && pa > 0;
  const walletNeedsAmount =
    (flags.appleOn && availability.applePayAvailable) ||
    (flags.googleOn && availability.googlePayAvailable);
  if (walletNeedsAmount && !hasInitAmount && !validated.isAmountEditable) {
    throw new PayrConfigError(
      'Wallet payments require init.pure_amount when isAmountEditable is not enabled.'
    );
  }

  initWalletButtonsRow(mainStack, {
    showApplePaySlot: flags.appleOn && availability.applePayAvailable,
    showGooglePaySlot: flags.googleOn && availability.googlePayAvailable,
    applePayAvailable: availability.applePayAvailable,
    onApplePayClick: wrapWalletButtonHandler(runApplePayFromWalletButton),
    googlePayMount:
      flags.googleOn && availability.googlePayAvailable
        ? {
            environment: availability.googlePayJsEnv,
            onGooglePayClick: wrapWalletButtonHandler(runGooglePayFromWalletButton),
          }
        : undefined,
  });
}

async function mountInitialPayContainerUi(options: {
  validated: ValidatedPayrSDKConfig;
  newConfig: ResolvedPayrSDKConfig;
  mainStack: HTMLElement;
  paymentMethodsHost: HTMLElement;
  paymentMethods: PaymentMethod[];
  generation: number;
}): Promise<void> {
  const { validated, newConfig, mainStack, paymentMethodsHost, paymentMethods, generation } =
    options;

  assertInitGenerationCurrent(generation);
  const flags: InitWalletFeatureFlags = {
    appleOn: validated.features?.applePay === true,
    googleOn: validated.features?.googlePay === true,
  };
  const apmsOn = validated.features?.apms === true;

  const availability = await probeInitWalletAvailability(flags, newConfig.environment, generation);
  mountInitWalletButtonsRow({ mainStack, validated, flags, availability });

  if (apmsOn) {
    initPaymentTypeDropdown(mainStack, {
      showApms: true,
      onChange: () => {
        void syncPaymentModeUiAfterTypeChange();
      },
    });
  }

  assertInitGenerationCurrent(generation);
  initPaymentMethodsDropdown(paymentMethodsHost, paymentMethods, () => {}, {
    onAddNew: openAddPaymentMethod,
    showLabel: false,
    onSelectedPaymentMethodChange: (m) => {
      updateMainLayoutCvvHelp(newConfig.containerElement, m?.schema);
    },
    onDeleteCardRequested: requestDeletePaymentMethod,
  });

  await applyInitialCvcAndRecurringLayout({
    validated,
    newConfig,
    mainStack,
    paymentMethodsCount: paymentMethods.length,
    generation,
  });
  assertInitGenerationCurrent(generation);
}

/**
 * Initialize the PAYR SDK
 */
async function init(config: PayrSDKConfig): Promise<void> {
  assertBrowserSupported();

  const validated = validateConfig(config);
  setActiveSdkAppearance(validated.resolvedAppearance);
  validated.containerElement.classList.add(PAYR_ROOT_CLASS);
  applyAppearanceToContainer(validated.containerElement, validated.resolvedAppearance);
  showSdkInitLoading(validated.containerElement);
  bumpInitGeneration();
  const generation = initGeneration;
  try {
    setDebugMode(validated.debug === true);
    validateToken(validated.authToken);

    const tenancies = await PayrApiClient.fetchTenancies(validated.apiBaseUrl, validated.authToken);
    assertInitGenerationCurrent(generation);
    const { tenancyId, selectedTenancy } = resolveTenancy(tenancies);

    const newClient = new PayrApiClient(validated.apiBaseUrl, validated.authToken, tenancyId);
    const { checkoutId, environment } = await resolveCheckoutIdAndEnvironment(validated, newClient);
    assertInitGenerationCurrent(generation);

    const sdkUrl = WORLDPAY_SDK_URLS[environment];

    const paymentFeeRate = resolvePaymentFeeRateFromEnv();

    const newConfig: ResolvedPayrSDKConfig = {
      ...validated,
      tenancyId,
      checkoutId,
      sdkUrl,
      environment,
      selectedTenancy,
      paymentFeeRate,
      currency: DEFAULT_SDK_CURRENCY,
    };

    await loadSDK(sdkUrl);
    assertInitGenerationCurrent(generation);
    const { mainStack, paymentMethodsHost } = ensurePayContainerLayout(newConfig.containerElement, {
      isAmountEditable: newConfig.isAmountEditable,
      prefillAmount: newConfig.pure_amount,
      onAmountChange: () => syncMainPaymentBreakdown(),
    });

    try {
      const paymentMethods = await newClient.getPaymentMethods();
      assertInitGenerationCurrent(generation);
      await loadInitialRecurringSnapshotForInit(newClient, validated, newConfig, generation);
      assertInitGenerationCurrent(generation);

      storedClient = newClient;
      storedConfig = newConfig;
      refreshRecurringUiImpl = refreshRecurringState;
      openAddPaymentMethodRef = openAddPaymentMethod;

      await mountInitialPayContainerUi({
        validated,
        newConfig,
        mainStack,
        paymentMethodsHost,
        paymentMethods,
        generation,
      });
      assertInitGenerationCurrent(generation);
      syncMainPaymentBreakdown();
    } catch (err) {
      removeAmountInputBlock(newConfig.containerElement);
      teardownInitOnInnerFailure(newConfig.containerElement, sdkUrl);
      throw err;
    }
    assertInitGenerationCurrent(generation);
    removeSdkInitLoading(newConfig.containerElement);
  } catch (err) {
    removeSdkInitLoading(validated.containerElement);
    if (storedConfig === null) {
      clearSdkAppearanceMount(validated.containerElement);
    }
    throw err;
  }
}

async function executeApmPayment(options: {
  storedConfig: ResolvedPayrSDKConfig;
  storedClient: PayrApiClient;
  pureAmount: number;
  amount: number;
  tenancyId: string;
  apmType: ApmType;
}): Promise<void> {
  const { storedConfig, storedClient, pureAmount, amount, tenancyId, apmType } = options;
  if (getRecurringToggleState()) {
    throw new PayrError(
      'Recurring payments are not supported for alternative payment methods.',
      PayrErrorCode.VALIDATION_ERROR
    );
  }
  const ref = storedConfig.reference?.trim();
  const customRef = ref || `PAYR_${Date.now()}`;
  const apmBody = {
    pure_amount: pureAmount,
    amount,
    tenancy_id: tenancyId,
    currency: storedConfig.currency,
    apm_type: apmType,
    custom_payment_reference: customRef,
    ...(ref ? { reference: ref } : {}),
  };
  const apmResult = await storedClient.createPayment(apmBody);
  const apmFinal = await completeApmPaymentFlow({
    apiClient: storedClient,
    apmType,
    createPaymentResult: apmResult,
    debug: storedConfig.debug,
  });
  storedConfig.onSuccess?.(apmFinal);
}

function resolveAmountFromConfig(
  config: ResolvedPayrSDKConfig,
  editableErrorMessage: string,
  fixedErrorMessage: string
): number {
  if (config.isAmountEditable) {
    touchAmountInput(config.containerElement);
    const isValid = validateAmountInput(config.containerElement, { revealAll: true });
    const live = getAmountInputValue(config.containerElement);
    if (!isValid || live == null) {
      throw new PayrError(editableErrorMessage, PayrErrorCode.VALIDATION_ERROR);
    }
    return live;
  }
  const pure = config.pure_amount;
  if (typeof pure !== 'number' || !Number.isFinite(pure) || pure <= 0) {
    throw new PayrError(fixedErrorMessage, PayrErrorCode.VALIDATION_ERROR);
  }
  return pure;
}

function resolveWalletPureAmount(config: ResolvedPayrSDKConfig): number {
  return resolveAmountFromConfig(
    config,
    'Please enter a valid payment amount before using wallet payments.',
    'Wallet payment requires init.pure_amount to be set.'
  );
}

async function runApplePayFromWalletButton(): Promise<void> {
  if (!storedConfig || !storedClient) {
    throw new PayrError('PAYR.init() must be called before Apple Pay.', PayrErrorCode.INIT_FAILED);
  }
  const config = storedConfig;
  const client = storedClient;
  const pureAmount = resolveWalletPureAmount(config);
  const amount = computeChargedAmountFromPure(pureAmount, config.paymentFeeRate);
  await executeApplePayPayment({
    storedConfig: config,
    storedClient: client,
    pureAmount,
    amount,
    tenancyId: config.tenancyId,
  });
}

async function runGooglePayFromWalletButton(): Promise<void> {
  if (!storedConfig || !storedClient) {
    throw new PayrError('PAYR.init() must be called before Google Pay.', PayrErrorCode.INIT_FAILED);
  }
  const config = storedConfig;
  const client = storedClient;
  const pureAmount = resolveWalletPureAmount(config);
  const amount = computeChargedAmountFromPure(pureAmount, config.paymentFeeRate);
  await executeGooglePayPayment({
    storedConfig: config,
    storedClient: client,
    pureAmount,
    amount,
    tenancyId: config.tenancyId,
  });
}

async function executeGooglePayPayment(options: {
  storedConfig: ResolvedPayrSDKConfig;
  storedClient: PayrApiClient;
  pureAmount: number;
  amount: number;
  tenancyId: string;
}): Promise<void> {
  const { storedConfig, storedClient, pureAmount, amount, tenancyId } = options;
  if (getRecurringToggleState()) {
    throw new PayrError(
      'Recurring payments are not supported for Google Pay.',
      PayrErrorCode.VALIDATION_ERROR
    );
  }
  const environment = resolveGooglePayJsEnvironment(storedConfig.environment);
  const result = await runGooglePayCheckout({
    apiClient: storedClient,
    pureAmount,
    chargedAmount: amount,
    tenancyId,
    environment,
    reference: storedConfig.reference,
  });
  storedConfig.onSuccess?.(result);
}

async function executeApplePayPayment(options: {
  storedConfig: ResolvedPayrSDKConfig;
  storedClient: PayrApiClient;
  pureAmount: number;
  amount: number;
  tenancyId: string;
}): Promise<void> {
  const { storedConfig, storedClient, pureAmount, amount, tenancyId } = options;
  if (getRecurringToggleState()) {
    throw new PayrError(
      'Recurring payments are not supported for Apple Pay.',
      PayrErrorCode.VALIDATION_ERROR
    );
  }
  const result = await runApplePayCheckout({
    apiClient: storedClient,
    pureAmount,
    chargedAmount: amount,
    tenancyId,
    reference: storedConfig.reference,
  });
  storedConfig.onSuccess?.(result);
}

async function executeCardPayment(options: {
  storedConfig: ResolvedPayrSDKConfig;
  storedClient: PayrApiClient;
  pureAmount: number;
  amount: number;
  tenancyId: string;
}): Promise<void> {
  const { storedConfig, storedClient, pureAmount, amount, tenancyId } = options;
  const reference = storedConfig.reference?.trim();

  const paymentMethodId = getSelectedPaymentMethodId();
  if (!paymentMethodId || paymentMethodId.trim() === '') {
    throw new PayrError(
      'No payment method selected. Please select a card.',
      PayrErrorCode.VALIDATION_ERROR
    );
  }

  const cvc_session_href = await generateCvcSession();

  const createBody = {
    pure_amount: pureAmount,
    amount,
    tenancy_id: tenancyId,
    currency: storedConfig.currency,
    token_id: paymentMethodId,
    cvc_session_href,
    ...(reference ? { reference } : {}),
    is_sdk: true as const,
    device_data: collectPaymentDeviceData(),
    three_ds: true as const,
    ...(getRecurringToggleState() ? { is_recurring: true as const } : {}),
  };
  const result = await storedClient.createPayment(createBody);
  const finalResult = await completePaymentWithOptional3ds({
    apiClient: storedClient,
    containerElement: storedConfig.containerElement,
    createPaymentResult: result,
    debug: storedConfig.debug,
  });
  storedConfig.onSuccess?.(finalResult);
}

/**
 * Destroy the SDK instance and clear stored state
 */
function destroy(): void {
  bumpInitGeneration();
  closeAddPaymentMethodModal();
  closeDeletePaymentMethodModal();
  closeRecurringPauseConfirmModal();
  closeRecurringManageModal();
  closePay3dsOverlay();
  closeApmPaymentOverlay();
  const containerEl = storedConfig?.containerElement;
  removeApplePayScript();
  removeGooglePayScript();
  removeWalletButtonsRow();
  removePaymentTypeDropdown();
  removePaymentMethodsDropdown();
  removeRecurringPaymentStrip();
  removeCvcCheckout();
  if (containerEl) {
    removeSdkInitLoading(containerEl);
    removeAmountInputBlock(containerEl);
    removePayContainerLayout(containerEl);
    containerEl.classList.remove(PAYR_ROOT_CLASS);
    clearAppearanceFromContainer(containerEl);
  }
  setActiveSdkAppearance(DEFAULT_SDK_APPEARANCE);
  if (storedConfig?.sdkUrl) {
    clearSDKCache(storedConfig.sdkUrl);
  }
  storedConfig = null;
  storedClient = null;
  refreshRecurringUiImpl = null;
  openAddPaymentMethodRef = null;
  recurringSnapshot = { list: [], details: null };
}

/**
 * Create a payment using the current SDK state.
 * Amount is taken from the amount input (when `isAmountEditable: true`) or from `init.pure_amount`.
 * Payment method is selected from the SDK-rendered dropdown. CVC is captured from the SDK CVC field.
 */
async function pay(): Promise<void> {
  if (!storedConfig || !storedClient) {
    throw new PayrError('PAYR.init() must be called before pay()', PayrErrorCode.INIT_FAILED);
  }

  const client = storedClient;
  const config = storedConfig;

  try {
    const pureAmount = resolvePayAmount(config);
    const amount = computeChargedAmountFromPure(pureAmount, config.paymentFeeRate);
    const tenancyId = config.tenancyId;

    const paymentType = getSelectedPaymentType();
    if (config.features?.apms === true && isApmType(paymentType)) {
      await executeApmPayment({
        storedConfig: config,
        storedClient: client,
        pureAmount,
        amount,
        tenancyId,
        apmType: paymentType,
      });
      return;
    }

    const recurringRequested = getRecurringToggleState();
    await executeCardPayment({
      storedConfig: config,
      storedClient: client,
      pureAmount,
      amount,
      tenancyId,
    });
    if (recurringRequested && config.features?.recurring === true) {
      await refreshRecurringUiImpl?.();
    }
  } catch (err) {
    config.onError?.(err instanceof Error ? err : new Error(String(err)));
    throw err;
  }
}

/**
 * Resolve the payment amount from the live input or from the stored config.
 * Validates and throws with a user-friendly error if no valid amount is available.
 */
function resolvePayAmount(config: ResolvedPayrSDKConfig): number {
  return resolveAmountFromConfig(
    config,
    'Please enter a valid payment amount before paying.',
    'Payment amount is not set. Provide init.pure_amount or use isAmountEditable.'
  );
}

/**
 * PAYR SDK public API
 */
export interface PayrSDK {
  init: (config: PayrSDKConfig) => Promise<void>;
  destroy: () => void;
  pay: () => Promise<void>;
}

const PAYR: PayrSDK = {
  init,
  destroy,
  pay,
};

// Export for module consumers (ESM/UMD)
export { PAYR, init, destroy, pay };
export type { PayrSDKConfig } from './config.js';
export type {
  PayrAppearanceInput,
  PayrWebSafeFontId,
  ResolvedSdkAppearance,
} from './theme/appearance.js';
export type {
  PaymentMethod,
  ApmType,
  SdkPaymentType,
  WalletType,
  ApplePayMerchantSession,
  ApplePayPaymentToken,
  PaymentDeviceData,
  RecurringPaymentDetails,
  RecurringPaymentListItem,
  UpdateRecurringPaymentRequest,
} from './types.js';
export { isApmType, isWalletType } from './types.js';
export { PayrConfigError, PayrError } from './types.js';
export { PayrSDKError } from './utils/errors.js';
export { logger, setDebugMode, enableSentry } from './utils/logger.js';
export { debounce, throttle } from './utils/performance.js';
export { PayrApiClient } from './api/client.js';
export {
  DEFAULT_PAYMENT_FEE_RATE,
  parsePaymentFeeRateString,
  resolvePaymentFeeRateFromEnv,
  computeChargedAmountFromPure,
  computeFeeAmountFromPure,
} from './utils/paymentFee.js';

// Default export: ESM `import PAYR from '…'`; UMD uses `src/umd-entry.ts` so `window.PAYR` is this object (Rollup `name: 'PAYR'`, `exports: 'default'`).
export default PAYR;
